import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import useUserStore from '@/stores/user.store';
import { modalStore } from '@/stores/modal.store';
import { chatStore } from '@/stores/chat.store';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import LevelBadge from '@/components/Levels/LevelBadge';
import BrandLogo from '@/components/Sidebar/BrandLogo';
import HoldBalance from '@/components/HoldBalance';
import { ApiRequest } from '@/utils/axios';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { openProfileModal } from '@/utils/profileModal';
import { login, authUrl } from '@/utils/auth';

interface NavEntry {
    href: string;
    label: string;
    /** Only show this entry to signed-in users. */
    authOnly?: boolean;
}

// Real, working routes — styled in the reference mockup's horizontal top-nav look.
const LINKS: NavEntry[] = [
    { href: '/', label: 'Jackpot' },
    { href: '/coinflip', label: 'Coinflip' },
    { href: '/casebattles', label: 'Case battles' },
    { href: '/marketplace', label: 'Market', authOnly: true },
    { href: '/fairplay', label: 'Fairplay' },
    { href: '/faq', label: 'FAQ' },
    { href: '/support', label: 'Support', authOnly: true },
];

const TopNav = () => {
    const pathname = usePathname();
    const { user, isAuthed, authv2 } = useUserStore((state) => state);
    const { sounds, setSound } = modalStore((state) => state);
    const { toggleChat } = chatStore((state) => state);

    const isActive = (href: string) =>
        href === '/' ? pathname === '/' : pathname?.startsWith(href);

    // Live "current pot" totals per mode, shown as a glowing badge next to the
    // nav link. Fetched on mount + polled (the nav is always mounted, so the
    // per-page stores aren't a reliable source); jackpot also updates live via
    // its socket so its fast-moving pot stays current between polls.
    const [totals, setTotals] = useState<Record<string, number | null>>({
        '/': null,
        '/coinflip': null,
        '/casebattles': null,
    });

    useEffect(() => {
        let alive = true;
        const fetchTotals = async () => {
            const [jp, cf, cb] = await Promise.allSettled([
                ApiRequest.get('/jackpot'),
                ApiRequest.get('/coinflip'),
                ApiRequest.get('/casebattle/list'),
            ]);
            if (!alive) return;
            setTotals((prev) => {
                const next = { ...prev };
                if (jp.status === 'fulfilled') {
                    next['/'] = Number(jp.value?.data?.response?.state?.value ?? 0);
                }
                if (cf.status === 'fulfilled') {
                    const games = cf.value?.data?.response?.games ?? [];
                    let s = 0;
                    for (const g of games) {
                        for (const it of g.firstItems ?? []) s += it.priceUsd ?? 0;
                        for (const it of g.secondItems ?? []) s += it.priceUsd ?? 0;
                    }
                    next['/coinflip'] = s;
                }
                if (cb.status === 'fulfilled') {
                    const battles = cb.value?.data?.response ?? [];
                    let s = 0;
                    for (const b of battles) {
                        const seats = (b.teamsCount ?? 1) * (b.teamsParticipantsCount ?? 1);
                        s += (b.priceUsd ?? 0) * seats;
                    }
                    next['/casebattles'] = s;
                }
                return next;
            });
        };
        fetchTotals();
        const iv = window.setInterval(fetchTotals, 7000);
        return () => {
            alive = false;
            window.clearInterval(iv);
        };
    }, []);

    // Snappy live jackpot pot between polls.
    useSubscription('jackpotValue', (v: number) =>
        setTotals((p) => ({ ...p, '/': Number(v) || 0 }))
    ).then();
    useSubscription('jackpotCreated', () => setTotals((p) => ({ ...p, '/': 0 }))).then();

    const lvl = isAuthed ? Math.floor((user.experience ?? 0) / 100) : 0;
    const balance = isAuthed ? user.balanceUsd?.toFixed(2) ?? '0.00' : null;

    return (
        <nav
            className="max-xl:hidden sticky top-0 z-50 flex items-center gap-2.5 px-6 h-[68px] border-b border-stroke"
            style={{
                // Опаковый фон вместо backdrop-filter: размытие на sticky-шапке
                // заставляло Chrome перерастеризовывать blur всего, что проезжает
                // под ней, на каждом кадре скролла — основная причина рывков.
                background:
                    'linear-gradient(180deg, rgba(20,15,42,0.97), rgba(14,10,30,0.97))',
                boxShadow:
                    '0 8px 40px -10px rgba(120,80,255,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
        >
            {/* Brand */}
            <Link href="/" className="flex items-center gap-2.5 pr-4 mr-1.5 shrink-0">
                <BrandLogo size={34} idSuffix="topnav" />
                <div className="flex flex-col leading-none">
                    <span className="font-extrabold tracking-[0.03em] text-[16px] text-text_main whitespace-nowrap">
                        RUSTY<span className="text-accent">XBET</span>
                    </span>
                    <span className="text-[9px] text-text_4 font-semibold tracking-[0.10em] mt-0.5">
                        .COM
                    </span>
                </div>
            </Link>

            {/* Nav links */}
            <div className="flex items-center gap-1.5 min-w-0">
                {LINKS.filter((l) => !l.authOnly || isAuthed).map((l) => {
                    const active = isActive(l.href);
                    return (
                        <Link
                            key={l.href}
                            href={l.href}
                            className={`flex items-center gap-2 px-[15px] py-[9px] rounded-fl text-[14.5px] font-semibold border transition-all ${
                                active
                                    ? 'text-white border-stroke_strong'
                                    : 'text-text_2 border-transparent hover:text-text_main hover:border-stroke'
                            }`}
                            style={
                                active
                                    ? {
                                          background: 'rgba(139,109,255,0.16)',
                                          boxShadow:
                                              '0 0 18px rgba(139,109,255,0.35), inset 0 0 10px rgba(139,109,255,0.18)',
                                      }
                                    : undefined
                            }
                        >
                            <span
                                className="w-[7px] h-[7px] rounded-full shrink-0"
                                style={
                                    active
                                        ? {
                                              background: '#3ad6ff',
                                              boxShadow: '0 0 10px #3ad6ff',
                                          }
                                        : { background: 'rgba(236,232,255,0.32)' }
                                }
                            />
                            {l.label}
                            {totals[l.href] != null && (totals[l.href] as number) > 0 && (
                                <span
                                    className="font-chat-nick ml-0.5 px-2 py-0.5 rounded-md text-[11px] font-extrabold tabular-nums leading-none"
                                    style={{
                                        color: '#ffe0a3',
                                        background: 'rgba(255,194,77,0.1)',
                                        border: '1px solid rgba(255,194,77,0.45)',
                                        boxShadow:
                                            '0 0 12px -1px rgba(255,194,77,0.7), inset 0 0 8px -3px rgba(255,194,77,0.6)',
                                        textShadow: '0 0 8px rgba(255,194,77,0.6)',
                                    }}
                                >
                                    ${(totals[l.href] as number).toFixed(2)}
                                </span>
                            )}
                        </Link>
                    );
                })}
            </div>

            <div className="flex-1" />

            {/* Sound toggle */}
            <button
                onClick={() => setSound()}
                aria-label="Toggle sounds"
                className="w-9 h-9 grid place-items-center rounded-fl bg-layer_2 border border-stroke text-text_2 hover:text-accent hover:border-stroke_strong transition-colors shrink-0"
            >
                <Image
                    src={sounds ? '/icons/sound.svg' : '/icons/sound_off.svg'}
                    alt=""
                    width={16}
                    height={16}
                />
            </button>

            {isAuthed ? (
                <>
                    {/* Balance */}
                    <div className="flex items-center gap-2.5 px-3.5 h-9 rounded-fl bg-layer_2 border border-stroke font-bold text-sm shrink-0">
                        <span
                            className="w-[18px] h-[18px] rounded-full grid place-items-center"
                            style={{
                                background:
                                    'radial-gradient(circle at 35% 30%, #ffe08a, #ffc24d)',
                                boxShadow: '0 0 12px rgba(255,194,77,0.7)',
                            }}
                        >
                            <span
                                className="text-[11px] font-black leading-none"
                                style={{
                                    color: '#6a4500',
                                    textShadow: '0 1px 0 rgba(255,255,255,0.45)',
                                }}
                            >
                                $
                            </span>
                        </span>
                        <b className="text-white tabular-nums">{balance}</b>
                    </div>

                    {/* Funds on hold (only when > 0) */}
                    <HoldBalance />

                    {/* Add funds */}
                    <Link
                        href="/deposit"
                        aria-label="Add funds"
                        className="w-9 h-9 grid place-items-center rounded-fl text-[#04150c] text-xl font-extrabold shrink-0 hover:brightness-110 transition"
                        style={{
                            background: 'linear-gradient(180deg, #46e08a, #2bb76b)',
                            boxShadow:
                                '0 0 18px rgba(70,224,138,0.6), inset 0 1px 0 rgba(255,255,255,0.4)',
                        }}
                    >
                        +
                    </Link>

                    {/* Withdraw */}
                    <Link
                        href="/withdraw"
                        aria-label="Withdraw"
                        className="w-9 h-9 grid place-items-center rounded-fl bg-layer_2 border border-stroke text-text_2 hover:text-accent hover:border-stroke_strong shrink-0 transition-colors"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-[18px] h-[18px] stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round]"
                        >
                            <path d="M12 4v10M8 10l4 4 4-4M5 20h14" />
                        </svg>
                    </Link>

                    {/* Chat toggle (compact viewports inside xl) */}
                    <button
                        onClick={() => toggleChat()}
                        aria-label="Chat"
                        className="2xl:hidden w-9 h-9 grid place-items-center rounded-fl bg-layer_2 border border-stroke text-text_2 hover:text-accent hover:border-stroke_strong transition-colors shrink-0"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-[18px] h-[18px] stroke-current fill-none [stroke-width:1.8] [stroke-linecap:round] [stroke-linejoin:round]"
                        >
                            <path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12z" />
                        </svg>
                    </button>

                    {/* Profile */}
                    <button
                        onClick={() => openProfileModal()}
                        className="flex items-center gap-2.5 pl-2.5 pr-3.5 h-9 rounded-fl bg-layer_2 border border-stroke hover:border-stroke_strong transition-colors shrink-0"
                    >
                        <AbstractAvatar
                            seed={user.name || String(user.id)}
                            src={user.avatar}
                            size={24}
                        />
                        <span className="text-[13px] font-semibold text-text_main max-w-[120px] truncate">
                            {user.name || 'Profile'}
                        </span>
                        <LevelBadge level={lvl} size="sm" />
                    </button>
                </>
            ) : (
                <Link
                    href={authUrl()}
                    onClick={(e) => {
                        e.preventDefault();
                        login();
                    }}
                    className={`${authv2 ? 'goAuth ' : ''}px-[22px] h-10 grid place-items-center rounded-fl text-white font-bold text-[14.5px] shrink-0 hover:brightness-110 transition`}
                    style={{
                        background: 'linear-gradient(180deg, #9b7dff, #6b46ff)',
                        boxShadow:
                            '0 0 22px rgba(139,109,255,0.6), inset 0 1px 0 rgba(255,255,255,0.35)',
                    }}
                >
                    Sign in
                </Link>
            )}
        </nav>
    );
};

export default TopNav;
